<!--
  Two screens live in this page. A coach answering their client's submitted check-up gets
  `cs-coach-checkup-review`, which draws its own header, content and buttons. Everyone else
  gets the check-up wizard below it. See `showsCoachReview()` for why the choice is made
  once, when the check-up loads, and never revisited.
-->
@if (showsCoachReview()) {
<cs-coach-checkup-review [checkup]="logWeeklyCheckup" [docWatcher]="docWatcher" [coachingDashboardOn]="coachingDashboardOn"
  trainingSettingsCallback="onCoachTrainingConfigurationResult"
  (resolved)="onCoachReviewResolved()"></cs-coach-checkup-review>
} @else {
<cs-header [backButton]="true" [fullWidth]="!isCompleted()" [separator]="true">
  <cs-header-title centered>
    {{ getPageTitle() }}
    @if (!isCompleted() && !isPendingCheckup()) {
    <cs-header-subtitle>
      {{ getStepLabel() }}
    </cs-header-subtitle>
    }
  </cs-header-title>

  @if (!isCompleted() && !isPendingCheckup()) {
  <cs-header-content>
    <cs-stepper [totalSteps]="totalProgressSteps" [currentStep]="progressStep"></cs-stepper>
  </cs-header-content>
  }
</cs-header>

@if (loading || isCompleted()) {
<cs-content [loading]="loading">
  @if (!loading && isAwaitingFromClient()) {
  <cs-card-list>
    <cs-info-card type="info" [message]="LL.coach.checkup.awaitingReview()" [dismissible]="false">
    </cs-info-card>
  </cs-card-list>
  }
  @if (!loading && isCompleted() && !isAwaitingCoach()) {
  <cs-card-list>
    @if (logWeeklyCheckup.completionAction) {
    <cs-card>
      <cs-card-title>
        {{ LL.weeklyCheckup.headers.checkupStatus() }}
      </cs-card-title>
      <cs-card-content>
        <div class="status-section">
          <cs-label class="section-label">{{ LL.weeklyCheckup.labels.completionStatus() }}</cs-label>
          <div class="section-value">
            <span class="value-text">{{ getCompletionActionLabel() }}</span>
          </div>
        </div>
      </cs-card-content>
    </cs-card>
    }
    <!-- Body composition: the overview cards while the coaching dashboard is on (#5456), else the charts row -->
    @if (coachingDashboardOn) {
    <!-- the card list places only cards, so the overview and its row go in a cs-card-list-row,
         which the list places and measures like a card. Without it the cards were drawn over them (#6597) -->
    <cs-card-list-row class="overview-block">
      <cs-body-comp-overview [model]="overviewModel" [loading]="overviewLoading" [failed]="overviewFailed"
        (retry)="loadCheckupOverviewAsync()">
      </cs-body-comp-overview>
      <!-- one row to the Training Overview, with how many exercises stood still or went down (#5456, phase 3) -->
      <div class="training-overview-row" [class.counts-loaded]="hasTrainingCounts()">
        <cs-button small secondary [label]="LL.trainingOverview.title()" (click)="onTrainingOverviewClick($event)"></cs-button>
        @if (trainingCountText(); as countText) {
        <cs-note class="training-count">{{ countText }}</cs-note>
        }
      </div>
    </cs-card-list-row>
    } @else {
    <cs-card-list-row>
      <cs-card class="chart-card">
        <cs-card-content>
          <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.weightTrend()"
            [unit]="weightUnit" [datasets]="weightTrendDatasets" [days]="trendDaysWeightIn" chartType="line" [showTunnel]="runningTrendOn"
            [tooltipInfo]="LL.weeklyCheckup.tooltips.weightTrend()" [positiveIsGood]="logWeeklyCheckup.isWeightGainGoal"
            (seeAllClick)="onWeightTrendSeeAllClick()">
          </cs-trend-chart>
        </cs-card-content>
      </cs-card>
      @if (showSkinfoldChart) {
      <cs-card class="chart-card">
        <cs-card-content>
          <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.skinfoldTrend()"
            [unit]="skinfoldUnit" [datasets]="skinfoldTrendDatasets" [days]="trendDaysWeightIn" chartType="line" [showTunnel]="runningTrendOn"
            [tooltipInfo]="LL.weeklyCheckup.tooltips.skinfoldTrend()"
            [positiveIsGood]="logWeeklyCheckup.isWeightGainGoal && isBodyCompositionTrendAcceptable"
            (seeAllClick)="onSkinfoldTrendSeeAllClick()">
          </cs-trend-chart>
        </cs-card-content>
      </cs-card>
      }
      @if (showTapeChart) {
      <cs-card class="chart-card">
        <cs-card-content>
          <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.waistTrend()"
            [unit]="waistUnit" [datasets]="waistTrendDatasets" [days]="trendDaysWeightIn" chartType="line" [showTunnel]="runningTrendOn"
            [tooltipInfo]="LL.weeklyCheckup.tooltips.waistTrend()"
            [positiveIsGood]="logWeeklyCheckup.isWeightGainGoal && isBodyCompositionTrendAcceptable"
            (seeAllClick)="onWaistTrendSeeAllClick()">
          </cs-trend-chart>
        </cs-card-content>
      </cs-card>
      }
    </cs-card-list-row>
    }

    <!-- Exercise progressions chart -->
    <cs-card class="chart-card">
      <cs-card-content>
        <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.exerciseProgressions()"
          [datasets]="workoutProgressionDatasets" [days]="trendDaysWorkout" chartType="bar"
          [tooltipInfo]="LL.weeklyCheckup.tooltips.exerciseProgressions()">
        </cs-trend-chart>
      </cs-card-content>
    </cs-card>
    @if (logWeeklyCheckup.bfData && logWeeklyCheckup.bfData.bf_pc) {
    <cs-card>
      <cs-card-title>
        {{ LL.weeklyCheckup.headers.bodyfatEstimation() }}
        <help-icon name="info" helpId="@help:references.body-composition"></help-icon>
      </cs-card-title>
      <cs-card-content>
        <div class="bodyfat-sections">
          <div class="status-section">
            <cs-label class="section-label">{{ LL.weeklyCheckup.labels.estimationMethod() }}</cs-label>
            <div class="section-value">
              <span class="value-text">{{ getBodyfatEstimationMethodName(logWeeklyCheckup.bfData.method) }}</span>
            </div>
          </div>
          <div class="status-section">
            <cs-label class="section-label">{{ LL.weeklyCheckup.labels.estimatedBodyfat() }}</cs-label>
            <div class="section-value">
              <span class="value-text">~{{ logWeeklyCheckup.bfData.bf_pc }}%</span>
            </div>
          </div>
        </div>
      </cs-card-content>
    </cs-card>
    }
    <cs-card>
      <cs-card-title>
        {{ LL.weeklyCheckup.headers.programUpdate() }}
        <help-icon name="info" helpId="@help:references.calorie-targets"></help-icon>
      </cs-card-title>
      <cs-card-content>
        @if (logWeeklyCheckup.atMinimumIntake) {
        <cs-note big light>
          {{ LL.weeklyCheckup.programUpdate.atMinimumCompleted() }}
        </cs-note>
        } @else if (logWeeklyCheckup.changeTooSmall) {
        <cs-note big light>
          {{ LL.weeklyCheckup.programUpdate.changeTooSmallCompleted() }}
        </cs-note>
        } @else if (logWeeklyCheckup.recommendedAvgCalories) {
        <cs-item-list>
          <cs-nutrition-stats-row [dailyAvg]="formatCompletedDailyAvgCalories()"
            [mealCalories]="formatCalories(logWeeklyCheckup.avgMealCalories)"
            [cutMealCalories]="formatCalories(logWeeklyCheckup.cutMealCalories)"
            [bulkMealCalories]="formatCalories(logWeeklyCheckup.bulkMealCalories)">
          </cs-nutrition-stats-row>
          @if (completedOwnTargetLine(); as ownTargetLine) {
          <cs-note light class="own-target-line">
            {{ ownTargetLine }}
          </cs-note>
          }
        </cs-item-list>
        } @else {
        <cs-note big light>
          {{ LL.weeklyCheckup.programUpdate.notEnoughDataCompleted() }}
        </cs-note>
        }
      </cs-card-content>
    </cs-card>
  </cs-card-list>
  }
</cs-content>
}

@if (!loading && !isCompleted() && isPendingCheckup()) {
<cs-content>
  <cs-card-list>
    @if (isFutureCheckup()) {
    <cs-info-card type="info" [message]="LL.weeklyCheckup.descriptions.futureCheckup()" [dismissible]="false">
    </cs-info-card>
    }
    @if (hasNewerCheckup) {
    <cs-info-card type="info" [message]="LL.weeklyCheckup.descriptions.outdatedCheckup()" [dismissible]="false">
    </cs-info-card>
    }
  </cs-card-list>
</cs-content>
}

@if (!loading && !isCompleted() && !isPendingCheckup()) {
<cs-tab-content-container class="step-content-container" [selection]="currentStep" vld-progress [vldStep]="progressStep"
  [vldTotalSteps]="totalProgressSteps" [vldDisableHiding]="true"
  [swipeEnabled]="hasClientSmallScreen()" (swipeForward)="handleNextButton()" (swipeBack)="handleBackButton()">
  <!-- Step 1: Intro -->
  <cs-tab-content [value]="WeeklyCheckupStep.Intro">
    <cs-content>
      <cs-card-list vld-group vldAutoScroll="true">
        <cs-card>
          <cs-card-content>
            <cs-note big light>
              {{ LL.weeklyCheckup.descriptions.intro() }}
            </cs-note>
          </cs-card-content>
        </cs-card>
      </cs-card-list>
    </cs-content>
  </cs-tab-content>
  <!-- Step 2: Progress Review -->
  <cs-tab-content [value]="WeeklyCheckupStep.ProgressReview">
    <cs-content>
      <div class="progress-content">
        <!-- Body composition: the overview cards while the coaching dashboard is on (#5456), else the charts row -->
        @if (coachingDashboardOn) {
        <cs-body-comp-overview [model]="overviewModel" [loading]="overviewLoading" [failed]="overviewFailed"
          (retry)="loadCheckupOverviewAsync()">
        </cs-body-comp-overview>
        <!-- one row to the Training Overview, with how many exercises stood still or went down (#5456, phase 3) -->
        <div class="training-overview-row" [class.counts-loaded]="hasTrainingCounts()">
          <cs-button small secondary [label]="LL.trainingOverview.title()" (click)="onTrainingOverviewClick($event)"></cs-button>
          @if (trainingCountText(); as countText) {
          <cs-note class="training-count">{{ countText }}</cs-note>
          }
        </div>
        } @else {
        <div class="charts-row">
          <cs-card class="chart-card">
            <cs-card-content>
              <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.weightTrend()"
                [unit]="weightUnit" [datasets]="weightTrendDatasets" [days]="trendDaysWeightIn" chartType="line" [showTunnel]="runningTrendOn"
                [tooltipInfo]="LL.weeklyCheckup.tooltips.weightTrend()"
                [positiveIsGood]="logWeeklyCheckup.isWeightGainGoal" (seeAllClick)="onWeightTrendSeeAllClick()">
              </cs-trend-chart>
            </cs-card-content>
          </cs-card>
          @if (showSkinfoldChart) {
          <cs-card class="chart-card">
            <cs-card-content>
              <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.skinfoldTrend()"
                [unit]="skinfoldUnit" [datasets]="skinfoldTrendDatasets" [days]="trendDaysWeightIn" chartType="line" [showTunnel]="runningTrendOn"
                [tooltipInfo]="LL.weeklyCheckup.tooltips.skinfoldTrend()"
                [positiveIsGood]="logWeeklyCheckup.isWeightGainGoal && isBodyCompositionTrendAcceptable"
                (seeAllClick)="onSkinfoldTrendSeeAllClick()">
              </cs-trend-chart>
            </cs-card-content>
          </cs-card>
          }
          @if (showTapeChart) {
          <cs-card class="chart-card">
            <cs-card-content>
              <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.waistTrend()"
                [unit]="waistUnit" [datasets]="waistTrendDatasets" [days]="trendDaysWeightIn" chartType="line" [showTunnel]="runningTrendOn"
                [tooltipInfo]="LL.weeklyCheckup.tooltips.waistTrend()"
                [positiveIsGood]="logWeeklyCheckup.isWeightGainGoal && isBodyCompositionTrendAcceptable"
                (seeAllClick)="onWaistTrendSeeAllClick()">
              </cs-trend-chart>
            </cs-card-content>
          </cs-card>
          }
        </div>
        }

        <!-- Weight explanation -->
        @if (weightInExplanationMessage) {
        <cs-info-card [type]="weightInExplanationType" [message]="weightInExplanationMessage" [dismissible]="false">
        </cs-info-card>
        }

        <!-- Exercise progressions chart -->
        <cs-card class="chart-card">
          <cs-card-content>
            <cs-trend-chart [loading]="trendingChartsLoading" [title]="LL.weeklyCheckup.charts.exerciseProgressions()"
              [datasets]="workoutProgressionDatasets" [days]="trendDaysWorkout" chartType="bar"
              [tooltipInfo]="LL.weeklyCheckup.tooltips.exerciseProgressions()">
            </cs-trend-chart>
          </cs-card-content>
        </cs-card>

        <!-- Training explanation -->
        @if (trainingProgressExplanationMessage) {
        <cs-info-card [type]="trainingProgressExplanationType" [message]="trainingProgressExplanationMessage"
          [dismissible]="false">
        </cs-info-card>
        }
      </div>
    </cs-content>
  </cs-tab-content>
  <!-- Step 3: Body Fat Estimation (conditional) -->
  @if (showBodyfatEstimationSection) {
  <cs-tab-content [value]="WeeklyCheckupStep.BodyfatEstimation">
    <cs-content>
      <cs-card-list #bodyfatEstimationCard="vldGroup" vld-group vldAutoScroll="true">
        <cs-card>
          <cs-card-content>
            <cs-item-list>
              <cs-note big light>{{ LL.weeklyCheckup.bodyfatEstimation.timeForEstimate() }}</cs-note>
              <cs-item-group>
                <cs-note>{{ LL.weeklyCheckup.bodyfatEstimation.bestEstimate() }}</cs-note>
                @if (bfData) {
                <bodyfat-input [(bfData)]="bfData" (bfDataChange)="bodyfatPercentChanged()"
                  [gender]="gender"></bodyfat-input>
                }
                <vld-message [validity]="validity('bf')"></vld-message>
              </cs-item-group>
              <cs-note big light>{{ LL.weeklyCheckup.bodyfatEstimation.skipOk() }}</cs-note>
            </cs-item-list>
          </cs-card-content>
        </cs-card>
      </cs-card-list>
    </cs-content>
  </cs-tab-content>
  }
  <!-- Step 4: Nutrition Logging -->
  <cs-tab-content [value]="WeeklyCheckupStep.NutritionLogging">
    <cs-content>
      <cs-card-list #nutritionLoggingCard="vldGroup" vld-group vldAutoScroll="true">
        <cs-card>
          <cs-card-content>
            <cs-item-list>
              <cs-note big light>{{ LL.weeklyCheckup.nutritionLogging.nextSection() }}</cs-note>
              <cs-item-group>
                <cs-note>{{ LL.weeklyCheckup.nutritionLogging.deselectIncomplete() }}</cs-note>
                <div class="weekdays-logs-chart">
                  @for (day of prevWeekLogDays; track day.dateISO; let i = $index) {
                  <div class="weekday" [ngClass]="{'checked': day.checked}">
                    <span class="weekday-target">{{ day.consumedCalories }}</span>
                    <div class="weekday-fill-bar-outer">
                      <div class="weekday-fill-bar" [style.height.%]="getOuterBarHeight(day)">
                        <div class="weekday-fill-bar-inner" [style.height.%]="getBarHeight(day)">
                        </div>
                        @if (day.consumedCalories > day.targetCalories) {
                        <div class="weekday-fill-bar-excess" [style.height.%]="getExcessBarHeight(day)">
                        </div>
                        }
                      </div>
                    </div>
                    <cs-button small square [toggle]="day.checked" [label]="day.weekdayLabel"
                      (click)="onLoggedDayCheckedChange(day)">
                    </cs-button>
                  </div>
                  }
                </div>
              </cs-item-group>
            </cs-item-list>
          </cs-card-content>
        </cs-card>
      </cs-card-list>
    </cs-content>
  </cs-tab-content>
  <!-- Step 5: Calorie Baseline -->
  <cs-tab-content [value]="WeeklyCheckupStep.CalorieBaseline">
    <cs-content>
      <cs-card-list #calorieBaselineCard="vldGroup" vld-group vldAutoScroll="true">
        <cs-card>
          <cs-card-content>
            <cs-item-list>
              <cs-note big light>
                {{ LL.weeklyCheckup.calorieBaseline.beforeAdjust() }}
                {{ LL.weeklyCheckup.calorieBaseline.whichOption() }}
              </cs-note>
              <cs-item-group>
                <cs-radio-group [(selected)]="selectedCalorieIntakeBaseline"
                  (selectedChange)="selectedCalorieIntakeBaselineChanged()">
                  <cs-radio [value]="CalorieIntakeBaseline.Logged">
                    {{ LL.weeklyCheckup.calorieBaseline.logged() }}
                    <cs-note>
                      {{ LL.weeklyCheckup.calorieBaseline.loggedNote() }}
                    </cs-note>
                    <cs-input-measure item-right [readonly]="true" [(canonValue)]="avgLoggedCalories"
                      type="nutrient_kcal"></cs-input-measure>
                  </cs-radio>
                  <cs-radio [value]="CalorieIntakeBaseline.Target">
                    {{ LL.weeklyCheckup.calorieBaseline.target() }}
                    <cs-note>
                      {{ LL.weeklyCheckup.calorieBaseline.targetNote() }}
                    </cs-note>
                    <cs-input-measure item-right [readonly]="true" [(canonValue)]="prevWeekRecommendedCalories"
                      type="nutrient_kcal"></cs-input-measure>
                  </cs-radio>
                  <cs-radio [value]="CalorieIntakeBaseline.Custom" (select)="customCalorieIntakeBaselineSelected()">
                    {{ LL.weeklyCheckup.calorieBaseline.custom() }}
                    <cs-note>
                      {{ LL.weeklyCheckup.calorieBaseline.customNote() }}
                    </cs-note>
                    <cs-input-measure item-right #customCalorieInput
                      [readonly]="selectedCalorieIntakeBaseline !== CalorieIntakeBaseline.Custom"
                      [(canonValue)]="customCalorieIntake" type="nutrient_kcal"></cs-input-measure>
                  </cs-radio>
                </cs-radio-group>
                <vld-message [validity]="validity('calorie-baseline')"></vld-message>
              </cs-item-group>
            </cs-item-list>
          </cs-card-content>
        </cs-card>
      </cs-card-list>
    </cs-content>
  </cs-tab-content>
  <!-- Step 6: Program Update -->
  <cs-tab-content [value]="WeeklyCheckupStep.ProgramUpdate">
    <cs-content>
      <cs-card-list vld-group vldAutoScroll="true">
        <cs-card>
          <cs-card-content>
            @if (isAdjustmentNeeded()) {
            @if (isCoachSubmissionMode()) {
            <cs-note big light>
              {{ LL.coach.checkup.willBeSentForReview() }}
            </cs-note>
            } @else {
            <cs-item-list>
              <cs-note big light>
                {{ LL.weeklyCheckup.programUpdate.basedOnProgress() }}
              </cs-note>
              <cs-nutrition-stats-row [dailyAvg]="formatDailyAvgCalories()"
                [mealCalories]="formatCalories(recommendedAvgMealCalories)"
                [cutMealCalories]="advancedMethodsCalorieCycling ? formatCalories(recommendedCutMealCalories) : undefined"
                [bulkMealCalories]="advancedMethodsCalorieCycling ? formatCalories(recommendedBulkMealCalories) : undefined">
              </cs-nutrition-stats-row>
              @if (clampedToMinimumIntake) {
              <cs-note light>
                {{ LL.weeklyCheckup.programUpdate.lowestTarget() }}
              </cs-note>
              }
            </cs-item-list>
            }
            } @else if (isAtMinimumIntake()) {
            <cs-note big light>
              {{ LL.weeklyCheckup.programUpdate.atMinimum() }}
            </cs-note>
            } @else if (isChangeTooSmall()) {
            <cs-note big light>
              {{ LL.weeklyCheckup.programUpdate.changeTooSmall() }}
            </cs-note>
            } @else if (isProgressUndetermined()) {
            <cs-note big light>
              @switch (undeterminedReason()) {
              @case (UndeterminedReason.MeasureMoreOften) {
              {{ LL.weeklyCheckup.programUpdate.undeterminedMeasureMoreOften() }}
              }
              @case (UndeterminedReason.InsufficientFatnessConfidence) {
              {{ LL.weeklyCheckup.programUpdate.undeterminedBodyFatTrendUnclear() }}
              }
              @case (UndeterminedReason.DataErratic) {
              {{ LL.weeklyCheckup.programUpdate.undeterminedDataErratic() }}
              }
              @case (UndeterminedReason.WeightTooFlat) {
              {{ LL.weeklyCheckup.programUpdate.undeterminedWeightTooFlat() }}
              }
              @default {
              {{ LL.weeklyCheckup.programUpdate.notEnoughData() }}
              }
              }
            </cs-note>
            } @else {
            <cs-note big light>
              {{ LL.weeklyCheckup.programUpdate.progressOnTrack() }}
            </cs-note>
            }
            <!-- #5456: with the coaching dashboard switch on, the choices are listed here and the bar keeps
                 Back and Confirm; the rule for which rows show is finalCheckupChoices -->
            @if (finalChoices(); as choices) {
            @if (choices.length > 0) {
            <cs-radio-group class="final-choices" [selected]="finalChoice" (selectedChange)="onFinalChoiceChange($event)">
              @for (choice of choices; track choice) {
              <cs-radio [value]="choice" [attr.data-choice]="choice" [disabled]="isFinishing">
                {{ finalChoiceLabel(choice) }}
                <span item-right class="choice-kcal">{{ finalChoiceKcal(choice) }}</span>
                <cs-note light>{{ finalChoiceHelp(choice) }}</cs-note>
              </cs-radio>
              @if (choice === CheckupFinalChoice.SetOwn && finalChoice === CheckupFinalChoice.SetOwn) {
              <!-- a person without a coach sets their own target, as kcal or as a percent of the intake they reported -->
              <cs-item-list class="own-target-fields">
                <div class="status-section">
                  <cs-label class="section-label">{{ LL.weeklyCheckup.ownTarget.intakeYouReported() }}</cs-label>
                  <div class="section-value">
                    <span class="value-text">{{ formatCalories(calorieIntakeBaseline) }}</span>
                  </div>
                </div>
                <div class="status-section">
                  <cs-label class="section-label">{{ LL.weeklyCheckup.ownTarget.recommendedTarget() }}</cs-label>
                  <div class="section-value">
                    <span class="value-text">{{ formatAppRecommendedWithChange() }}</span>
                  </div>
                </div>
                <cs-calorie-change-input #ownTargetInput [baseCalories]="calorieIntakeBaseline"
                  [minimumDailyIntake]="minimumDailyIntake" [defaultDirection]="defaultChangeDirection()"
                  [audience]="CalorieChangeAudience.Self" [calories]="modifyCalories"
                  (caloriesChange)="onOwnTargetCaloriesChange($event)"></cs-calorie-change-input>
                @if (isModifyUnchanged()) {
                <cs-note light>
                  {{ LL.weeklyCheckup.ownTarget.nothingToChange() }}
                </cs-note>
                }
                @if (userModifiedCalories !== undefined) {
                <!-- the meals of the solver's plan scaled to the typed number; Confirm saves this plan -->
                <cs-nutrition-stats-row class="own-target-stats" [dailyAvg]="formatOwnTargetDailyAvgCalories()"
                  [mealCalories]="formatCalories(ownTargetMealCalories().avg)"
                  [cutMealCalories]="advancedMethodsCalorieCycling ? formatCalories(ownTargetMealCalories().cut) : undefined"
                  [bulkMealCalories]="advancedMethodsCalorieCycling ? formatCalories(ownTargetMealCalories().bulk) : undefined">
                </cs-nutrition-stats-row>
                }
                @if (ownTargetBelowMinimumText(); as belowMinimum) {
                <cs-info-card type="warning" [dismissible]="false" [message]="belowMinimum"></cs-info-card>
                }
              </cs-item-list>
              }
              }
            </cs-radio-group>
            }
            }
          </cs-card-content>
        </cs-card>
      </cs-card-list>
    </cs-content>
  </cs-tab-content>
</cs-tab-content-container>
}

<!-- Action buttons at bottom -->
@if (!loading && !isCompleted() && !isPendingCheckup()) {
<cs-page-action-buttons [attr.data-step]="currentStep">
  <!-- Back button (steps 2+) -->
  @if (progressStep > 1) {
  <cs-button medium secondary [label]="LL.common.buttons.back()" (click)="handleBackButton()">
  </cs-button>
  }
  <!-- Skip button (bodyfat step only) -->
  @if (isBodyfatStep() && showBodyfatEstimationSection) {
  <cs-button medium secondary [label]="LL.common.buttons.skip()" (click)="handleSkipBodyfatButton()">
  </cs-button>
  }
  <!-- Start button (intro step only, when checkup is valid) -->
  @if (progressStep === 1 && !isFutureCheckup() && !hasNewerCheckup) {
  <cs-button medium primary [label]="LL.weeklyCheckup.buttons.start()" (click)="handleNextButton()">
  </cs-button>
  }
  <!-- Next button (steps 2 through second-to-last) -->
  @if (progressStep > 1 && !isLastStep()) {
  <cs-button medium primary [label]="LL.common.buttons.next()" [disabled]="isNextButtonDisabled()"
    (click)="handleNextButton()">
  </cs-button>
  }
  <!-- Final step: coached client with adjustment → Submit to Coach -->
  @if (isLastStep() && isCoachSubmissionMode()) {
  <cs-button medium primary [label]="LL.coach.checkup.submitToCoach()" (click)="onSubmitToCoachButtonClick($event)">
  </cs-button>
  }
  <!-- Final step, switch on (#5456): the choices are in the page; Confirm runs the picked one, Finish when there is none -->
  @if (isLastStep() && !isCoachSubmissionMode() && coachingDashboardOn) {
  @if (finalChoices().length > 0) {
  <cs-button medium primary class="confirm-final-choice" [label]="LL.common.buttons.confirm()"
    [disabled]="isConfirmDisabled()" (click)="onConfirmButtonClick($event)">
  </cs-button>
  } @else {
  <cs-button medium primary [label]="LL.common.buttons.finish()" (click)="onCheckupFinishButtonClick($event)">
  </cs-button>
  }
  }
  <!-- Final step, switch off: Decline/Cancel and Accept-or-Finish -->
  @if (isLastStep() && !isCoachSubmissionMode() && !coachingDashboardOn) {
  <cs-button medium secondary [label]="getDeclineOrCancelLabel()" (click)="onCheckupDeclineOrCancelButtonClick($event)">
  </cs-button>
  <cs-button medium primary [label]="getAcceptOrFinishLabel()" (click)="onCheckupFinishButtonClick($event)">
  </cs-button>
  }
</cs-page-action-buttons>
}
}
